/**
 * One line under the export buttons naming the UTC day a bundle covers and,
 * when the viewer's display zone isn't UTC, the same span in their time.
 * Bundles group records by UTC day, while the dashboard shows local times,
 * so a 9 PM record in New York lands in the next day's bundle.
 */
import type { AuditRecord } from '@sill/types';

import { useDisplayTimeZone } from '../../app/time-zone-context.js';
const sansFont = "'Geist', sans-serif";
import type { useC } from '../../lib/theme.js';
import { utcDayOf } from './export.js';

/** `"Oct 5 (UTC)"` plus `" · 8:00 PM Oct 4 – 8:00 PM Oct 5 your time"` off-UTC. */
export function bundleDayLabel(evaluated_at: string, timeZone: string): string {
  const day = utcDayOf(evaluated_at);
  const start = new Date(`${day}T00:00:00Z`);
  if (Number.isNaN(start.getTime())) return '';
  const end = new Date(start.getTime() + 86_400_000);
  const dayFmt = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
  const head = `Bundle day: ${dayFmt.format(start)} (UTC)`;
  let local: Intl.DateTimeFormat;
  try {
    local = new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZone,
    });
  } catch {
    return head;
  }
  const sameAsUtc = local.format(start) === new Intl.DateTimeFormat('en-US', {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: 'UTC',
  }).format(start);
  if (sameAsUtc) return head;
  return `${head} · ${local.format(start)} – ${local.format(end)} your time`;
}

export function BundleDayNote({
  C,
  record,
}: {
  C: ReturnType<typeof useC>;
  record: AuditRecord;
}): JSX.Element | null {
  const timeZone = useDisplayTimeZone();
  const label = bundleDayLabel(record.evaluated_at, timeZone);
  if (label === '') return null;
  return (
    <div
      data-testid="bundle-day-note"
      className="mt-2"
      style={{ fontFamily: sansFont, fontSize: 11, color: C.muted, lineHeight: 1.4 }}
    >
      {label}
    </div>
  );
}
